import {
	CONFLUENCE_ARI_PREFIX,
	type ConfluenceGraphQLSearchVariables,
	quickFindSearchQuery,
} from '@atlassian/dynamic-cards-search-client';

import type { SelectedFiltersType } from '../../../common/constants/types';
import type { SearchLinkProps } from '../../../common/ui/search-link';
import { getEmptySelectedFilters } from '../../../common/utils/filters';
import { type onError } from '../../error-boundary';
import { type TState } from '../../store/bootstrap';
import { quickSearchResultMapper } from './quick-search-result-mapper';

type FetchQuickSearchResultsArgs = {
	bootstrap: TState;
	experience: string;
	onError: onError;
	query: string;
	selectedEntities: string[];
	selectedFilters: SelectedFiltersType;
};

export const fetchQuickSearchResults = ({
	experience,
	query,
	bootstrap,
	onError,
	selectedFilters,
	selectedEntities,
}: FetchQuickSearchResultsArgs): Promise<
	| {
			duration: null;
			results: SearchLinkProps[];
	  }
	| {
			duration: null;
			results: never[];
	  }
> => {
	const { confluenceFilters: confluenceFiltersAll, commonFilters } =
		selectedFilters || getEmptySelectedFilters();

	// In this filter set, "range" is defined as an array but the QuickFindSearchQuery expects an object.
	const { range, ...restOfconfluenceFilters } = confluenceFiltersAll;
	const confluenceFilters = {
		...restOfconfluenceFilters,
		range:
			Array.isArray(range) && range.length > 0
				? (range[0] as ConfluenceGraphQLSearchVariables['confluenceFilters']['range'])
				: undefined,
	};

	const fetchData = async (query: string) => {
		const contentQueryResults = await quickFindSearchQuery({
			variables: {
				query,
				experience,
				cloudIdARI: `${CONFLUENCE_ARI_PREFIX}${bootstrap.cloudId}`,
				resultsLimit: 5,
				spaceLimit: 3,
				contentEntities: selectedEntities,
				confluenceFilters,
				commonFilters,
				includeAncestors: true,
				includeSpaces: false,
				includeAtlasGoals: false,
				includeAtlasProjects: false,
				searchSessionId: bootstrap.searchSessionId,
				queryVersion: 1,
			},
			aggAbsoluteUrl: bootstrap.aggAbsoluteUrl,
		});

		const contentResponse = contentQueryResults.data?.search.results?.edges || [];
		const contentResults = contentResponse.map(quickSearchResultMapper(query));
		return { results: contentResults, duration: null };
	};

	return fetchData(query).catch((error) => {
		onError(error);
		return { results: [], duration: null };
	});
};
